/**
 * Where the consultation looks: a company, a city, a point of sale, a depot.
 *
 * The scope is the application's — the back office keeps one context for
 * the whole session, shown in its header, and the catalogue is one of the
 * screens it drives. So this package declares only the *shape* it needs and
 * a hook to read and change it; the application injects the real one through
 * `catalogRoutes` (`useScope`). Without it, the screens keep a scope of
 * their own, which is a working screen rather than a broken one.
 *
 * The stock structure is injected the same way (`loadStructure`): companies,
 * points of sale and depots belong to the referential, and a feature may not
 * import a sibling feature.
 */

import { useState } from "react";

export interface ConsultationScope {
  /** Empty means every company. */
  companyCode: string;
  city: string;
  pointOfSaleCode: string;
  siteCode: string;
  /** Which articles to list: active ones, inactive ones, or all. Absent means all. */
  active?: "all" | "active" | "inactive";
}

export const EMPTY_SCOPE: ConsultationScope = {
  companyCode: "",
  city: "",
  pointOfSaleCode: "",
  siteCode: "",
};

export interface ScopeBinding {
  scope: ConsultationScope;
  setScope: (patch: Partial<ConsultationScope>) => void;
}

export interface StockStructure {
  companies: readonly { code: string; name: string }[];
  pointsOfSale: readonly { code: string; name: string; city: string }[];
  sites: readonly {
    code: string;
    name: string;
    companyCode: string;
    pointOfSaleCode: string;
    city: string;
  }[];
}

export const EMPTY_STRUCTURE: StockStructure = { companies: [], pointsOfSale: [], sites: [] };

/** A scope held by the screen itself, for when the application injects none. */
export function useLocalScope(): ScopeBinding {
  const [scope, setState] = useState<ConsultationScope>(EMPTY_SCOPE);
  return {
    scope,
    setScope: (patch) => {
      setState((current) => ({ ...current, ...patch }));
    },
  };
}

/** Every city a point of sale names, sorted, once. */
export function citiesOf(structure: StockStructure): string[] {
  const cities = new Set<string>();
  for (const point of structure.pointsOfSale) if (point.city) cities.add(point.city);
  for (const site of structure.sites) if (site.city) cities.add(site.city);
  return [...cities].sort((a, b) => a.localeCompare(b));
}

/**
 * The points of sale and depots that remain once the wider levels are set.
 *
 * A point of sale is kept when it is in the city and hosts at least one
 * depot of the company; a depot when it belongs to the company, sits in the
 * point of sale and in the city. Choosing a company therefore never offers a
 * depot of another one — which is how the context stays coherent without
 * clearing anything behind the reader's back.
 */
export function narrowStructure(
  structure: StockStructure,
  scope: ConsultationScope,
): { pointsOfSale: StockStructure["pointsOfSale"]; sites: StockStructure["sites"] } {
  const cityOf = new Map(structure.pointsOfSale.map((point) => [point.code, point.city]));
  const sites = structure.sites.filter((site) => {
    const city = cityOf.get(site.pointOfSaleCode) || site.city;
    return (
      (!scope.companyCode || site.companyCode === scope.companyCode) &&
      (!scope.pointOfSaleCode || site.pointOfSaleCode === scope.pointOfSaleCode) &&
      (!scope.city || city === scope.city)
    );
  });
  const hosting = new Set(
    structure.sites
      .filter((site) => !scope.companyCode || site.companyCode === scope.companyCode)
      .map((site) => site.pointOfSaleCode),
  );
  const pointsOfSale = structure.pointsOfSale.filter(
    (point) =>
      (!scope.city || point.city === scope.city) && (!scope.companyCode || hosting.has(point.code)),
  );
  return { pointsOfSale, sites };
}

/**
 * What a list filters by when each level may hold several values at once.
 *
 * Beside `ConsultationScope`, not instead of it: the header's working context
 * is one company, one city, one depot, and every screen reads it. A list that
 * lets the reader tick several keeps its own selection, *starts* from the
 * context (`selectionOf`), and does not write back to it — two values have no
 * single place to go in the header.
 *
 * Several values in one level are alternatives; different levels compose.
 */
export interface ScopeSelection {
  companyCodes: string[];
  cities: string[];
  pointOfSaleCodes: string[];
  siteCodes: string[];
}

export const EMPTY_SELECTION: ScopeSelection = {
  companyCodes: [],
  cities: [],
  pointOfSaleCodes: [],
  siteCodes: [],
};

/** The context as a selection: each set level becomes a choice of one. */
export function selectionOf(scope: ConsultationScope): ScopeSelection {
  return {
    companyCodes: scope.companyCode ? [scope.companyCode] : [],
    cities: scope.city ? [scope.city] : [],
    pointOfSaleCodes: scope.pointOfSaleCode ? [scope.pointOfSaleCode] : [],
    siteCodes: scope.siteCode ? [scope.siteCode] : [],
  };
}

/**
 * `narrowStructure`, for a selection: what remains once the wider levels are
 * set. A level with nothing chosen does not narrow.
 */
export function narrowStructureBy(
  structure: StockStructure,
  selection: ScopeSelection,
): { pointsOfSale: StockStructure["pointsOfSale"]; sites: StockStructure["sites"] } {
  const cityOf = new Map(structure.pointsOfSale.map((point) => [point.code, point.city]));
  const inCompany = (code: string) =>
    selection.companyCodes.length === 0 || selection.companyCodes.includes(code);
  const inCity = (city: string) => selection.cities.length === 0 || selection.cities.includes(city);

  const sites = structure.sites.filter((site) => {
    const city = cityOf.get(site.pointOfSaleCode) || site.city;
    return (
      inCompany(site.companyCode) &&
      (selection.pointOfSaleCodes.length === 0 ||
        selection.pointOfSaleCodes.includes(site.pointOfSaleCode)) &&
      inCity(city)
    );
  });
  const hosting = new Set(
    structure.sites
      .filter((site) => inCompany(site.companyCode))
      .map((site) => site.pointOfSaleCode),
  );
  const pointsOfSale = structure.pointsOfSale.filter(
    (point) =>
      inCity(point.city) && (selection.companyCodes.length === 0 || hosting.has(point.code)),
  );
  return { pointsOfSale, sites };
}

/**
 * Drop the choices a wider level no longer allows.
 *
 * Ticking a company must not leave a depot of another one chosen behind it —
 * the list would be scoped to nothing and the reader would not see why. Only
 * the narrower levels are pruned (points of sale, then depots); what the
 * reader chose at the level they just changed is never touched.
 */
export function pruneSelection(
  structure: StockStructure,
  selection: ScopeSelection,
): ScopeSelection {
  const points = narrowStructureBy(structure, {
    ...selection,
    pointOfSaleCodes: [],
    siteCodes: [],
  }).pointsOfSale;
  const pointOfSaleCodes = selection.pointOfSaleCodes.filter((code) =>
    points.some((point) => point.code === code),
  );
  const sites = narrowStructureBy(structure, {
    ...selection,
    pointOfSaleCodes,
    siteCodes: [],
  }).sites;
  const siteCodes = selection.siteCodes.filter((code) => sites.some((site) => site.code === code));
  return { ...selection, pointOfSaleCodes, siteCodes };
}
